import { useQuery } from "@tanstack/react-query";

import { fetchBlogPost, fetchBlogPostsList } from "./articles";
import { fetchPlants } from "./calculator";

/**
 * React Query hooks over the read-only API. The plant list and blog content
 * change rarely, so they are cached for the session: navigating away and back,
 * or between the blog list and a post, reuses the cache instead of refetching.
 * QueryProvider's 60s default is the floor; these opt into a longer window.
 */
const STATIC = { staleTime: 5 * 60_000, gcTime: 30 * 60_000 } as const;

/** The DEC plant catalogue (shared by the explorer wizard + solution redirects). */
export function usePlants() {
  return useQuery({ queryKey: ["plants"], queryFn: fetchPlants, ...STATIC });
}

/**
 * The blog listing. Locale rides the `Accept-Language` header (set by I18nProvider);
 * a locale switch invalidates this query centrally, so the key stays locale-free.
 */
export function useBlogPosts() {
  return useQuery({
    queryKey: ["blog", "list"],
    queryFn: fetchBlogPostsList,
    ...STATIC,
  });
}

/** Query options for a single blog post, shared by the hook + hover prefetch. */
export const blogPostQuery = (id: string) => ({
  queryKey: ["blog", "post", id],
  queryFn: () => fetchBlogPost(id),
  ...STATIC,
});

/** A single blog post by id; idle until an id is provided. */
export function useBlogPost(id: string | null | undefined) {
  return useQuery({ ...blogPostQuery(id as string), enabled: !!id });
}
